import { DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { take } from 'rxjs'

import { FlxModalConfig, FlxModalService } from '@flowx/angular-ui-toolkit'

import {
  ExportReviewModalData,
  ExportReviewOption,
  FlxExportReviewModalComponent,
} from '../export-review-modal/export-review-modal.component'
import { ExportNotification } from '../notifications.model'

/**
 * The review's "Change target version" modal. Escape closes it even before it has the focus, instead
 * of the notification center open under it.
 */
export const CHANGE_TARGET_VERSION_MODAL_CONFIG: FlxModalConfig = {
  backdropClass: 'overlay-backdrop',
  closeOnEscapeKey: true,
}

/** What each way out of the review does; every export type supplies its own. */
export type ExportReviewChoices = Record<ExportReviewOption, () => void>

export type OpenExportReview = (
  notification: ExportNotification,
  choices: ExportReviewChoices
) => void

/**
 * Opens the review of an export that left resources out, and runs the choice made in it. Shared by
 * the version and build export handlers; must be called in an injection context, like them.
 */
export const injectExportReview = (): OpenExportReview => {
  const modalService = inject(FlxModalService)

  return (notification, choices) => {
    const data: ExportReviewModalData = { notification }

    const reviewModal = modalService.show(
      FlxExportReviewModalComponent,
      { backdropClass: 'overlay-backdrop', closeOnBackdropClick: true, closeOnEscapeKey: true },
      data
    )

    // Ends with the modal too: one closed without picking an option (backdrop, Escape, navigation) never emits.
    reviewModal.data$
      .pipe(take(1), takeUntilDestroyed(reviewModal.componentRef.injector.get(DestroyRef)))
      .subscribe((option: ExportReviewOption) => {
        modalService.hide()
        choices[option]()
      })
  }
}
